
import { point } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { getCache, getCacheStale, setCache, CACHE_TTL } from './cacheService';
import type { SupportedCountry } from '../utils/countryDetection';

const PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const MET_ALERTS_URL = 'https://api.met.no/weatherapi/metalerts/2.0/current.json';
const SMHI_WARNINGS_URL = 'https://opendata-download-warnings.smhi.se/ibww/api/version/1/warning.json';

const CACHE_KEY_MET = 'fire_danger_met_v1';
const CACHE_KEY_SMHI = 'fire_danger_smhi_v1';

export type HazardType = 'forestFire' | 'avalanche' | 'flood' | 'landslide';

export interface FireDangerWarning {
  source: 'met' | 'smhi';
  country: 'NO' | 'SE';
  hazardType: HazardType;
  severity: 'Yellow' | 'Orange' | 'Red';
  title: string;
  description: string;
  instruction?: string;
  area: string;
  polygon?: GeoJSON.Feature;
  validFrom?: string;
  validTo?: string;
}

export interface FireDangerStatus {
  isInDangerZone: boolean;
  isGeneralBanActive: boolean;
  activeWarnings: FireDangerWarning[];
  highestSeverity: 'Yellow' | 'Orange' | 'Red' | null;
  dominantHazard: HazardType | null;
  /** Kartsenteret vurderingen ble gjort mot — brukes til «du er her»-prikk i mini-kartet. */
  center?: { lat: number; lon: number };
}

const MET_EVENT_TO_HAZARD: Record<string, HazardType> = {
  forestfire: 'forestFire',
  avalanches: 'avalanche',
  flood: 'flood',
  landslide: 'landslide',
};

const SMHI_EVENT_TO_HAZARD: Record<string, HazardType> = {
  FIRE: 'forestFire',
  HIGH_FLOW: 'flood',
  HIGH_WATER_LEVEL: 'flood',
  HIGH_FLOW_LAKE_AND_SEA: 'flood',
  EROSION_AND_LANDSLIDE_RISK: 'landslide',
};

const FALLBACK_TITLE: Record<HazardType, string> = {
  forestFire: 'Skogbrannfare',
  avalanche: 'Snøskredfare',
  flood: 'Flomfare',
  landslide: 'Jord- og flomskredfare',
};

/**
 * Check if the Norwegian general campfire ban is active (April 15 – September 15).
 */
export function isNorwayGeneralBanActive(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed: 3=April, 8=September
  const day = now.getDate();

  if (month > 3 && month < 8) return true;          // May – August
  if (month === 3 && day >= 15) return true;         // April 15+
  if (month === 8 && day <= 15) return true;         // September 1-15
  return false;
}

function mapMetSeverity(color: string | undefined): 'Yellow' | 'Orange' | 'Red' {
  if (color === 'Red') return 'Red';
  if (color === 'Orange') return 'Orange';
  return 'Yellow';
}

/**
 * Fetch forest fire warnings from MET MetAlerts (Norway).
 */
export async function fetchNorwayFireWarnings(): Promise<FireDangerWarning[]> {
  const cached = getCache(CACHE_KEY_MET);
  if (cached) return cached as FireDangerWarning[];

  const { data: staleData } = getCacheStale(CACHE_KEY_MET);

  try {
    const url = `${PROXY_URL}?url=${encodeURIComponent(MET_ALERTS_URL)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`MET status ${res.status}`);

    const json = await res.json();
    const features: any[] = json?.features ?? [];

    const warnings: FireDangerWarning[] = [];
    for (const f of features) {
      const eventCode = String(f.properties?.event || '').toLowerCase();
      const hazardType = MET_EVENT_TO_HAZARD[eventCode];
      if (!hazardType) continue;

      warnings.push({
        source: 'met',
        country: 'NO',
        hazardType,
        severity: mapMetSeverity(f.properties?.riskMatrixColor),
        title: f.properties?.eventAwarenessName || f.properties?.event || FALLBACK_TITLE[hazardType],
        description: f.properties?.description || f.properties?.consequences || '',
        instruction: f.properties?.instruction || '',
        area: f.properties?.area || f.properties?.geographicDomain || '',
        polygon: f.geometry ? { type: 'Feature' as const, properties: {}, geometry: f.geometry } : undefined,
        validFrom: f.when?.interval?.[0] || f.properties?.onset,
        validTo: f.when?.interval?.[1] || f.properties?.expires,
      });
    }

    setCache(CACHE_KEY_MET, warnings, CACHE_TTL.DEFAULT);
    return warnings;
  } catch (e) {
    console.warn('Failed to fetch MET fire warnings:', e);
    return (staleData as FireDangerWarning[]) ?? [];
  }
}

function mapSmhiSeverity(code: string | undefined): 'Yellow' | 'Orange' | 'Red' {
  if (code === 'RED') return 'Red';
  if (code === 'ORANGE') return 'Orange';
  return 'Yellow'; // YELLOW and MESSAGE both map to Yellow
}

function getSmhiDescription(descriptions: any[]): { text: string; instruction: string } {
  let text = '';
  let instruction = '';
  for (const d of descriptions) {
    const code = d?.title?.code;
    if (code === 'INCIDENT') text = d?.text?.en || d?.text?.sv || '';
    if (code === 'AFFECT') instruction = d?.text?.en || d?.text?.sv || '';
  }
  return { text, instruction };
}

/**
 * Fetch fire warnings from SMHI IBWW (Sweden).
 * Structure: array of warnings, each with event.code, warningAreas[] containing
 * warningLevel, areaName, descriptions[], and area.geometry (GeoJSON Polygon).
 */
export async function fetchSwedenFireWarnings(): Promise<FireDangerWarning[]> {
  const cached = getCache(CACHE_KEY_SMHI);
  if (cached) return cached as FireDangerWarning[];

  const { data: staleData } = getCacheStale(CACHE_KEY_SMHI);

  try {
    const url = `${PROXY_URL}?url=${encodeURIComponent(SMHI_WARNINGS_URL)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`SMHI status ${res.status}`);

    const json = await res.json();
    const items: any[] = Array.isArray(json) ? json : [];

    const warnings: FireDangerWarning[] = [];

    for (const w of items) {
      const eventCode = w.event?.code || '';
      const hazardType = SMHI_EVENT_TO_HAZARD[eventCode];
      if (!hazardType) continue;

      const eventTitle = w.event?.en || w.event?.sv || FALLBACK_TITLE[hazardType];

      for (const wa of w.warningAreas || []) {
        const { text, instruction } = getSmhiDescription(wa.descriptions || []);
        const geometry = wa.area?.geometry;

        warnings.push({
          source: 'smhi',
          country: 'SE',
          hazardType,
          severity: mapSmhiSeverity(wa.warningLevel?.code),
          title: eventTitle,
          description: text || wa.eventDescription?.en || wa.eventDescription?.sv || '',
          instruction: instruction || undefined,
          area: wa.areaName?.en || wa.areaName?.sv || '',
          polygon: geometry ? { type: 'Feature' as const, properties: {}, geometry } : undefined,
          validFrom: wa.approximateStart,
          validTo: wa.approximateEnd,
        });
      }
    }

    setCache(CACHE_KEY_SMHI, warnings, CACHE_TTL.DEFAULT);
    return warnings;
  } catch (e) {
    console.warn('Failed to fetch SMHI fire warnings:', e);
    return (staleData as FireDangerWarning[]) ?? [];
  }
}

/**
 * Evaluate fire danger status for a given map center position.
 */
export function evaluateFireDanger(
  lat: number,
  lon: number,
  country: SupportedCountry | null,
  warnings: FireDangerWarning[],
): FireDangerStatus {
  const isGeneralBanActive = country === 'NO' && isNorwayGeneralBanActive();

  const pt = point([lon, lat]); // Turf uses [lng, lat]
  const relevantWarnings: FireDangerWarning[] = [];

  for (const w of warnings) {
    if (w.polygon) {
      try {
        if (booleanPointInPolygon(pt, w.polygon as any)) {
          relevantWarnings.push(w);
        }
      } catch {
        // Invalid polygon geometry — skip
      }
    } else if (w.source === 'smhi' && country === 'SE') {
      // Fallback: if SMHI warning lacks polygon, include when viewport is in Sweden
      relevantWarnings.push(w);
    }
  }

  let highestSeverity: 'Yellow' | 'Orange' | 'Red' | null = null;
  let dominantHazard: HazardType | null = null;
  const order = { Yellow: 1, Orange: 2, Red: 3 };
  for (const w of relevantWarnings) {
    if (!highestSeverity || order[w.severity] > order[highestSeverity]) {
      highestSeverity = w.severity;
      dominantHazard = w.hazardType;
    }
  }

  return {
    isInDangerZone: relevantWarnings.length > 0,
    isGeneralBanActive,
    activeWarnings: relevantWarnings,
    highestSeverity,
    dominantHazard,
    center: { lat, lon },
  };
}
